import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, TextField, MenuItem, InputAdornment, IconButton } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ReferenceLine, Label
} from 'recharts';
import { apiService } from '../../services/apiService';

const filingOptions = [
  { value: 'M', label: 'Married Filing Jointly' },
  { value: 'S', label: 'Single' },
  { value: 'MS', label: 'Married Filing Separately' },
  { value: 'H', label: 'Head of Household' },
];

const MAX_SS_BENEFIT = 62172;
const MAX_SS_MSG = '2026 max benefit is $5,181/mo ($62,172/yr)';

const MtrGraph = ({ inputInfo, userInfo, retireYearData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filingStatus, setFilingStatus] = useState(inputInfo?.distribution_status || 'S');
  const [ssBenefit, setSsBenefit] = useState(inputInfo?.soc_sec_benefit || 36000);
  const [maxAlert, setMaxAlert] = useState(false);

  // Auto-dismiss max SS alert after 3 seconds
  useEffect(() => {
    if (maxAlert) {
      const timer = setTimeout(() => setMaxAlert(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [maxAlert]);

  // Sync from inputInfo on first load
  useEffect(() => {
    if (inputInfo?.distribution_status) setFilingStatus(inputInfo.distribution_status);
    if (inputInfo?.soc_sec_benefit) setSsBenefit(inputInfo.soc_sec_benefit);
  }, [inputInfo?.distribution_status, inputInfo?.soc_sec_benefit]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { filing_status: filingStatus, ss_benefit: ssBenefit };
        if (userInfo?.birth_date && inputInfo?.dist_start_age) {
          params.age = inputInfo.dist_start_age;
          // 2026-09-24: Spouse's own age in the same year, for the spouse's age-65 add (MFJ only)
          if (filingStatus === 'M' && userInfo?.birth_date_spouse) {
            const yearGap = parseInt(userInfo.birth_date.slice(0, 4), 10) - parseInt(userInfo.birth_date_spouse.slice(0, 4), 10);
            params.spouse_age = Number(inputInfo.dist_start_age) + yearGap;
          }
        }
        const result = await apiService.getMtrGraphData(params);
        setData(result);
      } catch (err) {
        setError('Failed to load MTR graph data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filingStatus, ssBenefit, inputInfo?.dist_start_age, userInfo?.birth_date, userInfo?.birth_date_spouse]);

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error && !data) return <Typography color="error">{error}</Typography>;
  if (!data) return null;

  const chartData = data.data.map(d => ({
    income: d.income / 1000,
    mtr: d.mtr * 100,
    mtr_adj: d.mtr_adj * 100,
    mtr_base: d.mtr * 100,
    ss_gap: (d.mtr_adj - d.mtr) * 100,
    dist_tax_rate: d.dist_tax_rate * 100,
    pct_ss_taxed: d.pct_ss_taxed,
  }));

  // Find torpedo zone boundaries (where mtr_adj > mtr)
  const torpedoStart = chartData.find(d => d.ss_gap > 0)?.income;
  const torpedoEnd = [...chartData].reverse().find(d => d.ss_gap > 0)?.income;

  // User's first distribution year "other income" = trad_dist + taxable_inc_when_distribute (pre-SS)
  const firstYearRow = (retireYearData && retireYearData.length > 0) ? retireYearData[0] : null;
  const otherTaxableInc = Number(inputInfo?.taxable_inc_when_distribute) || 0;
  const userOtherIncomeRaw = firstYearRow && firstYearRow.trad_dist_opt != null
    ? (Number(firstYearRow.trad_dist_opt) + otherTaxableInc) / 1000
    : null;
  const userOtherIncome = userOtherIncomeRaw ? Math.round(userOtherIncomeRaw) : null;
  const userOtherIncomeLabel = userOtherIncomeRaw ? (userOtherIncomeRaw).toFixed(1) : null;

  const filingLabel = filingOptions.find(o => o.value === data.filing_status)?.label || data.filing_status;

  return (
    <Box sx={{ mt: 1, mb: 0, border: '2px solid #b3e5fc', borderRadius: '8px', px: 0, pt: 0.5, pb: 1 }}>
      <Typography variant="subtitle1" sx={{ textAlign: 'center', mb: 0, fontWeight: 'bold' }}>
        Adjusted (Real) Marginal Tax Rates (${data.ss_benefit.toLocaleString()} Soc Sec Benefit - {filingLabel})
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', mb: 1, color: 'text.secondary' }}>
        {data.year} tax brackets, <Box component="span" sx={{ fontWeight: 'bold', color: '#000' }}>standard deduction: ${data.std_deduction.toLocaleString()}</Box>{data.std_deduction_65_add > 0 && ` ($${(data.std_deduction - data.std_deduction_65_add).toLocaleString()} + $${data.std_deduction_65_add.toLocaleString()} for age 65+)`}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
        <TextField
          select
          label="Filing Status"
          value={filingStatus}
          onChange={(e) => setFilingStatus(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          {filingOptions.map(o => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ position: 'relative' }}>
        <TextField
          label="SS Benefit"
          value={ssBenefit.toLocaleString()}
          onChange={(e) => {
            const num = Number(e.target.value.replace(/,/g, ''));
            if (!isNaN(num)) {
              if (num > MAX_SS_BENEFIT) {
                setMaxAlert(true);
              } else {
                setSsBenefit(num);
              }
            }
          }}
          size="small"
          sx={{ width: 180 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', flexDirection: 'column', mr: -1 }}>
                  <IconButton size="small" sx={{ p: 0 }} onClick={() => setSsBenefit(b => {
                    if (b + 1000 > MAX_SS_BENEFIT) { setMaxAlert(true); return b; }
                    return b + 1000;
                  })}>
                    <ArrowDropUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" sx={{ p: 0 }} onClick={() => setSsBenefit(b => Math.max(0, b - 1000))}>
                    <ArrowDropDownIcon fontSize="small" />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
        />
        {maxAlert && (
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', ml: 1 }}>
            {MAX_SS_MSG}
          </Typography>
        )}
        </Box>
      </Box>
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="income"
            tickFormatter={(val) => `$${val}k`}
            ticks={[0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240]}
            label={{ value: 'Other Income', position: 'insideBottom', offset: 0, fill: '#000', fontWeight: 'bold' }}
            height={35}
          />
          <YAxis
            tickFormatter={(val) => `${val}%`}
            domain={[0, 50]}
            ticks={[0, 10, 20, 30, 40, 50]}
          />
          <RechartsTooltip
            offset={25}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const order = ['MTR Adjusted for SS', 'Marginal Tax Rate (MTR)', 'Effective Tax Rate'];
              const filtered = payload.filter(e => order.includes(e.name));
              const sorted = [...filtered].sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
              return (
                <div style={{ backgroundColor: 'white', border: '1px solid #ccc', padding: '8px 12px', borderRadius: 4 }}>
                  {sorted.map((entry, i) => {
                    const mtrEntry = sorted.find(e => e.name === 'Marginal Tax Rate (MTR)');
                    const mtrAdj = sorted.find(e => e.name === 'MTR Adjusted for SS');
                    const multiplier = mtrEntry?.value > 0 ? (mtrAdj.value / mtrEntry.value) : 0;
                    return (<React.Fragment key={i}>
                      <div style={{ color: entry.color, fontSize: 13 }}>
                        {entry.value.toFixed(1)}% - {entry.name}
                      </div>
                      {entry.name === 'Marginal Tax Rate (MTR)' && multiplier > 1 && (
                        <div style={{ fontSize: 12, color: '#d32f2f', fontWeight: 'bold' }}>
                          {multiplier.toFixed(2)}x - Rate Multiplier
                        </div>
                      )}
                    </React.Fragment>);
                  })}
                  {(() => {
                    const income = label * 1000;
                    const pi = data.ss_benefit / 2 + income;
                    const dataPoint = payload?.[0]?.payload;
                    const taxableSS = Math.round(data.ss_benefit * (dataPoint?.pct_ss_taxed || 0));
                    const taxableIncome = Math.max(0, income + taxableSS - data.std_deduction);
                    const zone = data.ss_zones?.find(z => pi >= z.min && (z.max === null || pi <= z.max));
                    const zonePct = zone ? zone.pct : 0;
                    const zoneMin = zone ? `$${zone.min.toLocaleString()}` : '';
                    // For 85% zone with no IRS upper bound, compute practical cap where taxable SS = 0.85 * benefit
                    let zoneMaxLabel = zone?.max ? `$${zone.max.toLocaleString()}` : 'No limit';
                    if (zone && zone.pct === 85 && zone.max === null && data.ss_benefit > 0) {
                      const zone50 = data.ss_zones.find(z => z.pct === 50);
                      if (zone50) {
                        const max50ZoneSS = 0.5 * (zone.min - zone50.min);
                        const piCap = zone.min + (0.85 * data.ss_benefit - Math.min(max50ZoneSS, 0.5 * data.ss_benefit)) / 0.85;
                        zoneMaxLabel = `$${Math.round(piCap).toLocaleString()}`;
                      }
                    }
                    return (<>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Taxable Income: ${taxableIncome.toLocaleString()} (<strong style={{ color: '#000' }}>${income.toLocaleString()}</strong> + ${taxableSS.toLocaleString()} SS {'\u2013'} ${data.std_deduction.toLocaleString()} std ded)
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        Provisional Income: ${pi.toLocaleString()} (SS/2 ${(data.ss_benefit / 2).toLocaleString()} + ${income.toLocaleString()})
                      </div>
                      <div style={{ fontSize: 12, color: (zonePct > 0 && dataPoint?.mtr_adj > dataPoint?.mtr) ? '#d32f2f' : '#666' }}>
                        SS Zone: {zonePct}% (PI {zoneMin}{'\u2013'}{zoneMaxLabel})
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        SS Taxed: {((dataPoint?.pct_ss_taxed || 0) * 100).toFixed(1)}% (${taxableSS.toLocaleString()} of ${data.ss_benefit.toLocaleString()})
                      </div>
                    </>);
                  })()}
                </div>
              );
            }}
          />
          <Legend verticalAlign="bottom" content={({ payload }) => (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, paddingTop: 8 }}>
              {payload.filter(e => !['mtr_base', 'ss_gap'].includes(e.dataKey)).map((entry) => (
                <span key={entry.dataKey} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <svg width="30" height="10">
                    <line x1="0" y1="5" x2="30" y2="5"
                      stroke={entry.color}
                      strokeWidth={entry.payload.strokeWidth || 2}
                      strokeDasharray={entry.payload.strokeDasharray || 'none'} />
                  </svg>
                  {entry.value}
                </span>
              ))}
            </div>
          )} />
          <Area type="stepAfter" dataKey="mtr_base" stackId="shade" fill="transparent" stroke="none" dot={false} legendType="none" tooltipType="none" isAnimationActive={false} />
          <Area type="stepAfter" dataKey="ss_gap" stackId="shade" fill="rgba(255,150,150,0.4)" stroke="none" dot={false} legendType="none" tooltipType="none" isAnimationActive={false} />
          <Line type="stepAfter" dataKey="mtr_adj" name="MTR Adjusted for SS" stroke="#d32f2f" strokeDasharray="8 4" dot={false} strokeWidth={3} />
          <Line type="stepAfter" dataKey="mtr" name="Marginal Tax Rate (MTR)" stroke="#000000" strokeDasharray="2 3" dot={false} strokeWidth={3} />
          <Line type="monotone" dataKey="dist_tax_rate" name="Effective Tax Rate" stroke="#7b1fa2" dot={false} strokeWidth={2} />
          {torpedoStart != null && torpedoEnd != null && (
            <ReferenceLine x={Math.round((torpedoStart + torpedoEnd) / 2)} stroke="none">
              <Label value="SS Torpedo Zone" position="insideTop" fill="#d32f2f" fontSize={13} fontWeight="bold" dy={15} />
            </ReferenceLine>
          )}
          {userOtherIncome != null && userOtherIncome <= 250 && (
            <ReferenceLine x={userOtherIncome} stroke="#1976d2" strokeWidth={2} strokeDasharray="6 3"
              label={({ viewBox }) => (
                <text x={viewBox.x + 8} y={viewBox.y + 15} fill="#1976d2" fontSize={12} fontWeight="bold" textAnchor="start">
                  &#x2190; Pre-conversion Other Income: ${userOtherIncomeLabel}k
                </text>
              )} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {torpedoStart != null && (
        <>
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#d32f2f', fontWeight: 'bold' }}>
            Torpedo Zone (${torpedoStart}k–${torpedoEnd}k)
          </Typography>
          <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', color: '#666' }}>
            Income in this range is taxed at amplified rates due to Social Security taxation
          </Typography>
        </>
      )}
    </Box>
  );
};

export default MtrGraph;
